import { AVATAR_COLORS, getFaculty } from "@clubap/shared";
import { useEffect, useRef, useState } from "react";
import type { Account } from "../api.js";
import { AVATAR_ART } from "../art/manifest.js";
import { AvatarPreview } from "./AvatarPreview.js";
import { IconMoneda } from "./icons.js";

interface Props {
  account: Account;
  /** Ids de ítem puestos, para que la foto enseñe lo que llevas ahora mismo. */
  worn: readonly string[];
  onOpenAccount: () => void;
  onOpenProfile: () => void;
  /**
   * Ir a tu iglú. `null` cuando ya estás dentro: el cuarto propio es uno solo.
   *
   * Va en el carnet y no en el dock por lo mismo que "Administración": el dock son las
   * cosas que se hacen jugando en la sala donde estás, y esto es de tu cuenta — es
   * literalmente irte a tu casa.
   */
  onOpenIgloo: (() => void) | null;
  /** Sólo se ofrece con cuenta dev y en desarrollo; el panel lo decide App. */
  onOpenShapes: (() => void) | null;
  /** Igual que el anterior: colocar ítems reescribe código fuente. */
  onOpenItems: (() => void) | null;
  /** Sólo con la marca `admin`. Null lo esconde; el permiso real lo pone el servidor. */
  onOpenAdmin: (() => void) | null;
  /** Las solicitudes para fundar clubes. Sólo un admin las decide. */
  onOpenClubProposals: (() => void) | null;
  onLogout: () => void;
}

/**
 * El carnet: quién eres, de qué facultad y cuánto tienes.
 *
 * Ocupa la esquina superior izquierda y es además la entrada a la cuenta y al
 * perfil. Que sea una sola cosa es la decisión: antes el nombre de la sala, el
 * saldo y el menú de cuenta eran tres controles sueltos, y el carnet es
 * exactamente el objeto que en un campus ya reúne foto, facultad e identidad.
 */
export function Carnet({
  account,
  worn,
  onOpenAccount,
  onOpenProfile,
  onOpenIgloo,
  onOpenShapes,
  onOpenItems,
  onOpenAdmin,
  onOpenClubProposals,
  onLogout,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  /*
   * Un clic fuera cierra el menú.
   *
   * El menú tapa parte del mundo y el mundo se juega a clics: sin esto, abrirlo por
   * error dejaba una tarjeta encima del suelo y el clic que la cerraría se lo comía
   * ella. Se escucha en `pointerdown` y no en `click` para cerrarlo antes de que el
   * canvas procese el toque.
   */
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const color = AVATAR_COLORS[account.color] ?? AVATAR_COLORS[0];
  const swatch = `#${color.toString(16).padStart(6, "0")}`;
  const faculty = getFaculty(account.faculty);

  return (
    <div className="carnet-wrap" ref={wrapRef}>
      <button
        className="carnet"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        title="Tu carnet"
      >
        <span className="carnet__franja" aria-hidden="true" />

        <span className="carnet__foto" style={{ background: swatch }}>
          {AVATAR_ART ? (
            /*
             * Tu tortuga con lo que lleve puesto, y no la inicial del nombre.
             *
             * El carnet es lo único que enseña tu aspecto sin abrir un panel, y una
             * letra no dice si te pusiste la gorra que acabas de comprar. Es la MISMA
             * figura de la tienda y del inventario, así que lo que se compra y lo que
             * se lleva se ven iguales en los tres sitios.
             */
            <AvatarPreview worn={worn} color={account.color} encuadre="cabeza" />
          ) : (
            // Sin arte de avatar no hay nada que recortar; docs/ART.md dice cómo agregarlo.
            <span className="carnet__inicial">{account.username.slice(0, 1).toUpperCase()}</span>
          )}
        </span>

        <span className="carnet__datos">
          <span className="carnet__institucion">
            ESPOL{faculty ? ` · ${faculty.code}` : ""}
          </span>
          <span className="carnet__nombre">{account.username}</span>
          <span className="carnet__creditos">
            <IconMoneda />
            {account.coins} CRÉDITOS
          </span>
        </span>

        {/* El punto es el único aviso de que la recuperación no está lista. Sin él,
            "no tengo correo puesto" es justo lo que nadie descubre hasta el día que
            ya no puede entrar a arreglarlo. */}
        {!account.emailVerified && <span className="carnet__aviso" aria-hidden="true" />}
      </button>

      {open && (
        <div className="carnet__menu">
          <button
            className="carnet__item"
            type="button"
            onClick={() => {
              setOpen(false);
              onOpenAccount();
            }}
          >
            Mi cuenta
            {!account.emailVerified && <span className="carnet__punto" aria-hidden="true" />}
          </button>

          <button
            className="carnet__item"
            type="button"
            onClick={() => {
              setOpen(false);
              onOpenProfile();
            }}
          >
            Mi perfil
          </button>

          {onOpenIgloo && (
            <button
              className="carnet__item"
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenIgloo();
              }}
            >
              Mi iglú
            </button>
          )}

          {onOpenItems && (
            <button
              className="carnet__item"
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenItems();
              }}
            >
              Ítems de personalización
            </button>
          )}

          {onOpenShapes && (
            <button
              className="carnet__item"
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenShapes();
              }}
            >
              Colisión de dibujos
            </button>
          )}

          {/*
            Administración va en el menú del carnet y no en el dock a propósito: el
            dock son las cuatro cosas que se hacen jugando, y esto no se hace jugando.
            Aquí abajo vive lo de tu cuenta y tus herramientas, que es lo que es.
          */}
          {onOpenAdmin && (
            <button
              className="carnet__item"
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenAdmin();
              }}
            >
              Administración
            </button>
          )}

          {onOpenClubProposals && (
            <button
              className="carnet__item"
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenClubProposals();
              }}
            >
              Clubes por aprobar
            </button>
          )}

          {import.meta.env.DEV && <span className="carnet__tecla">F2 · editor de mapas</span>}

          <button className="carnet__salir" type="button" onClick={onLogout}>
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
